import { ReactNode, useState, createContext, useContext } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../providers/AuthProvider'
import { DateRangePicker } from './DateRangePicker'
import { ExportMenu } from './ExportMenu'
import { getDefaultDateRange } from '../lib/date-utils'
import type { Granularity } from '../lib/types'

interface LayoutProps {
  children: ReactNode
}

const navItems = [
  { to: '/', label: 'Overview' },
  { to: '/traffic', label: 'Traffic' },
  { to: '/features', label: 'Features' },
  { to: '/geography', label: 'Geography' },
  { to: '/devices', label: 'Devices' },
]

// Date range context for child components
interface DateRangeContextType {
  startDate: string
  endDate: string
  granularity: Granularity
  setDateRange: (range: { start_date: string; end_date: string }) => void
  setGranularity: (g: Granularity) => void
}

export const DateRangeContext = createContext<DateRangeContextType | null>(null)

export function useDateRange() {
  const context = useContext(DateRangeContext)
  if (!context) {
    // Return default if not in context (for standalone usage)
    const defaults = getDefaultDateRange()
    return {
      startDate: defaults.start_date,
      endDate: defaults.end_date,
      granularity: 'day' as Granularity,
      setDateRange: () => {},
      setGranularity: () => {},
    }
  }
  return context
}

const granularityOptions: Granularity[] = ['hour', 'day', 'week', 'month']

export function Layout({ children }: LayoutProps) {
  const { logout } = useAuth()
  const location = useLocation()
  const [dateRange, setDateRange] = useState(getDefaultDateRange())
  const [granularity, setGranularity] = useState<Granularity>('day')

  // Show granularity selector only on Traffic page
  const showGranularity = location.pathname === '/traffic'

  return (
    <DateRangeContext.Provider
      value={{
        startDate: dateRange.start_date,
        endDate: dateRange.end_date,
        granularity,
        setDateRange,
        setGranularity,
      }}
    >
      <div className="min-h-screen bg-secondary/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <h1 className="text-xl font-semibold text-foreground">
                Analytics Dashboard
              </h1>

              <div className="flex items-center gap-4">
                {showGranularity && (
                  <div className="flex gap-1 bg-secondary/50 rounded-lg p-1">
                    {granularityOptions.map((g) => (
                      <button
                        key={g}
                        onClick={() => setGranularity(g)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          granularity === g
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
                <DateRangePicker value={dateRange} onChange={setDateRange} />
                <ExportMenu />
                <button
                  onClick={logout}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex gap-1 -mb-px">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      isActive
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </header>

        {/* Main content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </main>
      </div>
    </DateRangeContext.Provider>
  )
}
